// PromptModal.tsx
"use client";
import { Search } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";

const PromptModal = ({ open, history, onClose, onUpdate }) => {
    const [localPrompts, setLocalPrompts] = useState(history || []);

    useEffect(() => {
        setLocalPrompts(history || []);
    }, [history, open]);

    const removePromptAt = (index) => {
        setLocalPrompts((prev) => {
            const next = [...prev];
            next.splice(index, 1);
            return next;
        });
    };

    const handleUpdate = () => {
        onUpdate(localPrompts);
    };

    const displayPrompts = useMemo(() => {
        return [...localPrompts].reverse();
    }, [localPrompts]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center px-4">
            <div
                className="absolute inset-0 bg-black/40"
                onClick={onClose}
                aria-hidden="true"
            />

            <div className="relative z-[2100] max-w-4xl w-full bg-white rounded-md border border-dashed border-blue-200 shadow-lg overflow-hidden">
                <div className="flex items-center gap-3 px-6 pt-6 pb-2">
                    <Search size={20} color="#1c8ea8" />
                    <div className="flex-1">
                        <h3 className="text-xl font-semibold">AI Search Filters</h3>
                    </div>
                </div>
                <div className="px-6 pb-6">
                    <p className="text-sm text-gray-600 mt-1">
                        These are all the prompts you've added to shape your search. The profile results are based on a mix of these.
                    </p>
                    <p className="text-sm text-gray-600">
                        Click the <span className="font-semibold">×</span> on any card to remove it from search logic and update your search results.
                    </p>
                </div>

                <div className="px-6 pb-6">
                    <div className="space-y-3">
                        {displayPrompts.length === 0 && (
                            <div className="py-8 text-center text-gray-500">No prompts present.</div>
                        )}

                        {displayPrompts.map((promptText, displayIndex) => {
                            const originalIndex = localPrompts.length - 1 - displayIndex;
                            const promptNumber = originalIndex + 1;
                            const isMostRecent = displayIndex === 0;

                            return (
                                <div
                                    key={originalIndex}
                                    className="relative rounded-md border border-gray-200 bg-white p-4"
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs text-gray-400">✦ Prompt {promptNumber}</span>
                                                {isMostRecent && <span className="text-xs text-gray-400 ml-2">(Most recent)</span>}
                                            </div>
                                            <p className="text-sm text-gray-700 mt-2 break-words">{promptText}</p>
                                        </div>

                                        <button
                                            onClick={() => removePromptAt(originalIndex)}
                                            aria-label={`Remove prompt ${promptNumber}`}
                                            className="ml-4 shrink-0 rounded-full p-1.5 bg-white border border-red-100 hover:bg-red-50"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-red-500">
                                                <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="flex justify-end gap-4 px-6 py-4 border-t">
                    <button
                        onClick={onClose}
                        className="text-sm text-primary hover:underline px-3 py-2 rounded"
                    >
                        Back
                    </button>

                    <button
                        onClick={handleUpdate}
                        disabled={JSON.stringify(localPrompts) === JSON.stringify(history)}
                        className={` text-sm px-4 py-2 rounded ${JSON.stringify(localPrompts) === JSON.stringify(history)
                            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                            : "bg-primary text-white hover:brightness-95"
                            }`}
                    >
                        Update Search Results
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PromptModal;
